<template>
  <div class="irrigation-details-page">
    <breadcrumb :items="breadcrumbs"></breadcrumb>

    <v-container fluid class="page-shell px-6 py-4">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center flex-wrap mb-6" style="gap: 16px;">
        <div>
          <h1 class="page-title mb-0">{{ $t('esgPlatform.assessmentFarmer.irrigationDetails') }}</h1>
        </div>
        <v-btn
          color="#00BD73"
          dark
          class="text-caption font-weight-bold px-6"
          elevation="0"
          @click="goBack"
        >
          {{ $t("back") || "BACK" }}
        </v-btn>
      </div>

      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="#00BD73" size="48"></v-progress-circular>
      </div>

      <v-alert v-else-if="!payload" type="info" text border="left" class="mb-0">
        {{ $t('esgPlatform.assessmentFarmer.noIrrigationDetails') }}
      </v-alert>

      <template v-else>
        <!-- Farmer / Company Card -->
        <v-card flat class="info-card pa-6 mb-6">
          <div class="d-flex justify-space-between align-start flex-wrap" style="gap: 16px;">
            <div>
              <h2 class="info-heading mb-1">{{ farmerDisplayName }}</h2>
              <p class="info-sub mb-0">{{ $t('esgPlatform.assessmentFarmer.trackingContributions') }}</p>
            </div>
            <!-- Irrigation Count Badge -->
            <div class="irrigation-badge">
              <div class="irrigation-badge__count">{{ irrigationCount }}</div>
              <div class="irrigation-badge__label">{{ $t('esgPlatform.assessmentFarmer.irrigationCount') }}</div>
            </div>
          </div>

          <!-- Total Water Volume -->
          <v-card flat class="water-card mt-6 pa-5 d-inline-flex align-center flex-column" style="gap: 16px;">
            <div class="d-flex align-center">
              <v-icon color="#127EE4" size="28">mdi-water-outline</v-icon>
              <div class="water-label mb-1">{{ $t('esgPlatform.assessmentFarmer.totalWaterVolumeUsed') }}</div>
            </div>
            <div>
              <div class="d-flex align-baseline" style="gap: 6px;">
                <span class="water-value">{{ totalWaterVolume.toLocaleString() }}</span>
                <span class="water-unit">{{ $t('esgPlatform.assessmentFarmer.liters') }}</span>
              </div>
            </div>
          </v-card>
                  <!-- Irrigation Count Details -->
        <v-card flat class="detail-card">
          <v-expansion-panels v-model="detailsPanel" flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="detail-panel-header pa-0 my-5">
                <span class="detail-panel-title ps-4">{{ $t('esgPlatform.assessmentFarmer.irrigationCountDetails') }}</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="px-2 pt-2">
                  <v-col
                    v-for="(row, idx) in irrigationRows"
                    :key="row.id || idx"
                    cols="12"
                    sm="6"
                    class="pa-3"
                  >
                    <v-card flat class="record-card pa-5">
                        <div class="record-date mb-4">{{ formatDate(getLatestIrrigationDate(row)) }}</div>                      
                        <div class="record-grid">
                            <div
                                v-for="(val, key) in getIrrigationDisplayFields(row)"
                                :key="key"
                                class="record-field"
                            >
                                <span class="record-label">{{ formatFieldLabel(key) }}:</span>
                                <span class="record-value">{{ formatFieldValue(key, val) }}</span>
                            </div>
                        </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
        </v-card>


      </template>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";

const STORAGE_KEY = "assessmentFarmerIrrigationDetails";

export default {
  name: "assessmentFarmerIrrigationDetails",
  data() {
    return {
      loading: true,
      payload: null,
      detailsPanel: 0,
      breadcrumbs: [
        {
          text: this.$t("esgPlatform.esgCompliance"),
          link: true,
          exact: true,
          disabled: true,
        },
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "eudrDashboard" },
        },
      ],
    };
  },
  computed: {
    irrigationRows() {
      return Array.isArray(this.payload?.irrigationRows) ? this.payload.irrigationRows : [];
    },
    irrigationCount() {
      return this.irrigationRows.length;
    },
    totalWaterVolume() {
      return this.irrigationRows.reduce((sum, row) => {
        const vol = parseFloat(row.waterVolumeUsed);
        return sum + (Number.isFinite(vol) ? vol : 0);
      }, 0);
    },
    farmerDisplayName() {
      return this.payload?.farmerName || "Gold Farms";
    },
  },
  mounted() {
    this.loadDetails();
  },
  methods: {
    loadDetails() {
      try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (this.matchesCurrentRoute(parsed)) {
            this.payload = parsed;
          }
        }
      } catch {
        this.payload = null;
      } finally {
        this.loading = false;
      }

      if (!this.payload) {
        this.$notify({
          type: "warning",
          text: this.$t("esgPlatform.assessmentFarmer.noIrrigationDetailsNotification"),
        });
      }
    },
    matchesCurrentRoute(payload) {
      if (!payload) return false;
      return (
        String(payload.assessmentId || "") === String(this.$route.params.assessmentId || "") &&
        String(payload.goalId || "") === String(this.$route.params.goalId || "") &&
        String(payload.submittedById || "") === String(this.$route.params.submittedById || "")
      );
    },
    goBack() {
      this.$router.go(-1);
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("DD MMMM YYYY");
    },
    formatNumber(value) {
      if (value == null) return "-";
      const n = parseFloat(value);
      return Number.isFinite(n) ? n.toLocaleString() : "-";
    },
    formatCropType(row) {
      const cropType = row.irrigation_cropType || row.harvest_cropType;
      if (!cropType) return "-";
      if (Array.isArray(cropType)) return cropType.join(", ") || "-";
      return cropType;
    },
    getLatestIrrigationDate(row) {
        const dates = row['irrigation_dates'];
        if (Array.isArray(dates) && dates.length) {
            return dates.reduce((latest, d) => (d > latest ? d : latest));
        }
        return row['date-of-entry'];
    },
    getIrrigationDisplayFields(row) {
        const skipKeys = new Set(['id', 'irrigation_farm', 'irrigation_dates', 'date-of-entry', 'waterSource']);
        return Object.fromEntries(
            Object.entries(row).filter(([key, val]) => 
                !skipKeys.has(key) && val !== null && val !== undefined && val !== ''
            )
        );
    },
    formatFieldLabel(key) {
        const translationKey = `esgPlatform.dimitraModuleCatalog.fields.${key}`;
        const translated = this.$t(translationKey);

        if (translated !== translationKey) {
          return translated;
        }

        return key.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    formatFieldValue(key, val) {
        if (Array.isArray(val)) return val.join(', ') || '-';
        if (key === 'waterVolumeUsed') return this.$t('esgPlatform.assessmentFarmer.litersLabel', { value: this.formatNumber(val) });
        if (key === 'area' || key === 'irrigatedArea') return this.$t('esgPlatform.assessmentFarmer.acresLabel', { value: val });
        return val;
    },
  },
};
</script>

<style scoped>
.irrigation-details-page {
  background: #f1f4f7;
  min-height: 100vh;
}
.page-shell {
  max-width: 100%;
}
.page-caption {
  color: #7d8a93;
  font-size: 14px;
  font-weight: 500;
}
.page-title {
  color: #101828;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

/* Info card */
.info-card {
  border-radius: 8px;
}
.info-heading {
  color: #212121;
  font-size: 22px;
  font-weight: 700;
}
.info-sub {
  color: #7d8a93;
  font-size: 14px;
}

/* Irrigation count badge */
.irrigation-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #00BD73;
  border-radius: 10px;
  padding: 14px 22px;
  min-width: 90px;
  text-align: center;
}
.irrigation-badge__count {
  color: #ffffff;
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
}
.irrigation-badge__label {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Water volume card */
.water-card {
  border: 1px solid #e8f5e9;
  border-radius: 10px;
  background: #f9fffe;
}
.water-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #e8f5e9;
  border-radius: 50%;
  flex-shrink: 0;
}
.water-label {
  color: #7d8a93;
  font-size: 14px;
  font-weight: 500;
}
.water-value {
  color: #212121;
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
}
.water-unit {
  color: #616161;
  font-size: 16px;
  font-weight: 500;
}

/* Detail expansion panel */
.detail-card {
  border-radius: 8px;
  overflow: visible;
}
.detail-panel-header {
  border-bottom: 1px solid #eef2ef;
  border-radius: 0;
  background-color: #E3E3E3;
  min-height: 40px;
}
.detail-panel-title {
  color: #212121;
  font-size: 15px;
  font-weight: 700;
}

/* Per-record cards */
.record-card {
  border: 1px solid #e8edf0;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 2px 10px 0px #50505029 !important;
}
.record-date {
  color: #212121;
  font-size: 16px;
  font-weight: 700;
}
.record-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.record-field {
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.record-label {
  color: #212121;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  margin-right: 8px;
}
.record-value {
  color: #616161;
  font-weight: 400;
  font-size: 14px;
}

@media (max-width: 700px) {
  .page-title {
    font-size: 22px;
  }
  .water-value {
    font-size: 28px;
  }
  .irrigation-badge__count {
    font-size: 28px;
  }
}
</style>
